import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import BgText from "./bgText";
import { useState } from "react";
import sendMessage from "../../api/sendMesage";
import { CheckCircle } from "@mui/icons-material";

const ContactMe = () => {
  const isMob = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [message, setMessage] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("Message sent successfully");
  const [severity, setSeverity] = useState("success");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submitMessage = () => {
    if (email && fullName && message) {
        setSending(true);
        sendMessage({ fullName, email, message })
          .then(() => {
            setSent(true);
            setSending(false);
            setAlertMessage("Message sent successfully");
            setSeverity("success");
            setAlertOpen(true);
          })
          .catch(() => {
            setAlertMessage("Something went wrong");
            setSeverity("error");
            setAlertOpen(true);
            setSending(false);
          });
    } else {
      setAlertMessage("Please fill out all fields");
      setSeverity("error");
      setAlertOpen(true);
    }
  };

  return (
    <Stack
      sx={{
        width: "100%",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        py: 10,
        zIndex: 1
      }}
      id="contactMe"
    >
      <BgText>Contact</BgText>
      
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ mt: 4 }}>
          {/* Socials Column */}
          <Grid 
            item xs={12} md={4}
            component={motion.div}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <Stack spacing={4}>
              <Box>
                <Typography variant="h3" fontWeight={700} color="white" gutterBottom>
                  Get in touch
                </Typography>
                <Typography variant="body1" color="rgba(255, 255, 255, 0.6)">
                  Have a project in mind? Let's build something amazing together.
                </Typography>
              </Box>
              
              <Stack spacing={2}>
                {[
                  { name: 'Linkedin', url: 'https://www.linkedin.com/in/daniel-maina-8449bb23b/' },
                  { name: 'Github', url: 'https://github.com/danielobima' },
                  { name: 'Email', url: 'mailto:mail@danielobima.com' }
                ].map((link) => (
                  <Box 
                    key={link.name} 
                    component="a" 
                    href={link.url}
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: 2, 
                        borderRadius: '16px',
                        textDecoration: 'none',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.05)',
                        background: 'rgba(255,255,255,0.02)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            background: 'rgba(255,255,255,0.05)',
                            borderColor: '#22d3ee',
                            transform: 'translateX(8px)'
                        }
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>{link.name}</Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Grid>

          {/* Form Column */}
          <Grid item xs={12} md={8}>
            <Box 
              className="glass-card" 
              sx={{ p: { xs: 3, md: 6 }, height: '100%', position: 'relative' }}
              component={motion.div}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            >
              <AnimatePresence mode="wait">
                {!sent ? (
                  <Stack spacing={4} key="form">
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                      <TextField
                        fullWidth
                        label="Full name"
                        variant="outlined"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        sx={{ input: { color: 'white' }, '& label': { color: 'rgba(255,255,255,0.5)' } }}
                      />
                      <TextField
                        fullWidth
                        label="Email"
                        variant="outlined"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ input: { color: 'white' }, '& label': { color: 'rgba(255,255,255,0.5)' } }}
                      />
                    </Stack>
                    <TextField
                      fullWidth
                      multiline
                      rows={5}
                      label="Your message"
                      variant="outlined"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { color: 'white' }, '& label': { color: 'rgba(255,255,255,0.5)' } }}
                    />
                    <Button 
                      onClick={submitMessage} 
                      disabled={sending}
                      variant="contained"
                      size="large"
                      sx={{ 
                        py: 2, 
                        fontWeight: 700, 
                        fontSize: '1.1rem',
                        background: 'linear-gradient(45deg, #22d3ee, #818cf8)',
                        color: '#020617',
                        borderRadius: '12px',
                        '&:hover': { background: 'linear-gradient(45deg, #22d3ee, #818cf8)', opacity: 0.9 }
                      }}
                    >
                      {sending ? <CircularProgress size={24} sx={{ color: '#020617' }} /> : "Send Message"}
                    </Button>
                  </Stack>
                ) : (
                  <Box 
                    key="success"
                    component={motion.div}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    sx={{ textAlign: 'center', py: 10 }}
                  >
                    <CheckCircle sx={{ fontSize: 80, color: '#22d3ee', mb: 4 }} />
                    <Typography variant="h4" color="white" fontWeight={700} gutterBottom>
                      Message Sent!
                    </Typography>
                    <Typography variant="body1" color="rgba(255,255,255,0.6)">
                      Thanks for reaching out. I'll get back to you soon.
                    </Typography>
                  </Box>
                )}
              </AnimatePresence>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Typography variant="body2" color="rgba(255,255,255,0.3)" sx={{ mt: 10 }}>
        © {new Date().getFullYear()} Daniel Obima. All rights reserved.
      </Typography>

      <Snackbar open={alertOpen} autoHideDuration={6000} onClose={() => setAlertOpen(false)}>
        <Alert severity={severity}>{alertMessage}</Alert>
      </Snackbar>
    </Stack>
  );
};

export default ContactMe;
